import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { icmTruncationNote } from '../icmPaging';
import { fetchTransactionHistory, downloadLabelReport, type TransactionHistoryRow } from './icmTrackingApi';
import '../../backlog/backlog.css';
import '../alerts/icmAlerts.css';

/**
 * Inventory Management → Tracking → Item Transaction History.
 *
 * Every lot that has been assigned an internal LT# — its whole receive →
 * distribute → consume → dispose lifecycle in one grid. Ported from ALPACA
 * `Distribution_ListView_LTsearch` (criteria `Not IsNullOrEmpty([LT])`),
 * which is read-only apart from one legacy action: `ICMLTReportController`'s
 * "Label Report" (`LTReportAction`), which prints the checked row(s) as a
 * one-page-per-lot PDF (QR + Item Name + LT No + Dist Date + Exp Date +
 * Storage). Reproduced here as a QuestPDF document — see
 * `IIcmLtLabelPdfGenerator`.
 */
export default function ItemTransactionHistoryPage() {
  const notify = useNotify();

  const [rows, setRows] = useState<TransactionHistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchTransactionHistory();
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
    } catch {
      setError('Failed to load item transaction history.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.lt ?? '').toLowerCase().includes(t) ||
      (r.vendorLt ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  // Column set + order from the LIVE ALPACA model (Distribution_ListView_LTsearch,
  // BTLIMS.Web/Model.xafml — visible ColumnInfo nodes in Index order; Division
  // is omitted because this grid is already scoped to the current division):
  // LT# · Item · Description · Catalog# · Vendor · Category · Grade · Department
  // (RQID.department) · RequestedDate · RequestedBy · ReviewedDate · ReviewedBy ·
  // PurchaseDate · PurchaseBy · ReceiveDate · ReceivedBy · DistributionDate ·
  // DistributedBy · ConsumptionDate · ConsumptionBy · DisposedDate · DisposedBy ·
  // RQ# · PO# · RC# · VendorLT · ExpirationDate · PackUnits · UnitPrice.
  // Captions are G5's. Specification is captioned "Description" on the Items BO.
  // Status is hidden in legacy (Index -1), so it is not shown here (Bug 22).
  // 2026-09-29: restored the 7 legacy columns an earlier pass dropped and
  // corrected "Given By" → "Distributed By" (a different Distribution column).
  const cols: AppColumn<TransactionHistoryRow>[] = [
    { field: 'lt',                header: 'LT#',         width: 110, text: r => r.lt ?? '—', render: r => r.lt ?? '—' },
    { field: 'itemName',          header: 'Item',        width: 200, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'specification',     header: 'Description',   width: 200, text: r => r.specification ?? '—', render: r => r.specification ?? '—' },
    { field: 'catalog',           header: 'Catalog#',    width: 110, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'vendorName',        header: 'Vendor',      width: 140, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'categoryName',      header: 'Category',    width: 120, text: r => r.categoryName ?? '—', render: r => r.categoryName ?? '—' },
    { field: 'gradeName',         header: 'Grade',       width: 100, text: r => r.gradeName ?? '—', render: r => r.gradeName ?? '—' },
    { field: 'departmentName',    header: 'Department',  width: 130, text: r => r.departmentName ?? '—', render: r => r.departmentName ?? '—' },
    { field: 'requestedDate',     header: 'RequestedDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.requestedDate) ?? '—', render: r => fmtLocalDateTime(r.requestedDate) ?? '—' },
    { field: 'requestedByName',   header: 'RequestedBy',  width: 140, text: r => r.requestedByName ?? '—', render: r => r.requestedByName ?? '—' },
    { field: 'reviewedDate',      header: 'ReviewedDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.reviewedDate) ?? '—', render: r => fmtLocalDateTime(r.reviewedDate) ?? '—' },
    { field: 'reviewedByName',    header: 'ReviewedBy',  width: 140, text: r => r.reviewedByName ?? '—', render: r => r.reviewedByName ?? '—' },
    { field: 'purchaseDate',      header: 'PurchaseDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.purchaseDate) ?? '—', render: r => fmtLocalDateTime(r.purchaseDate) ?? '—' },
    { field: 'purchaseByName',    header: 'PurchaseBy',   width: 140, text: r => r.purchaseByName ?? '—', render: r => r.purchaseByName ?? '—' },
    { field: 'receiveDate',       header: 'ReceiveDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.receiveDate) ?? '—', render: r => fmtLocalDateTime(r.receiveDate) ?? '—' },
    { field: 'receivedByName',    header: 'ReceivedBy',  width: 140, text: r => r.receivedByName ?? '—', render: r => r.receivedByName ?? '—' },
    { field: 'distributionDate',  header: 'DistributionDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.distributionDate) ?? '—', render: r => fmtLocalDateTime(r.distributionDate) ?? '—' },
    { field: 'distributedByName', header: 'DistributedBy',  width: 140, text: r => r.distributedByName ?? '—', render: r => r.distributedByName ?? '—' },
    { field: 'consumptionDate',   header: 'ConsumptionDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.consumptionDate) ?? '—', render: r => fmtLocalDateTime(r.consumptionDate) ?? '—' },
    { field: 'consumptionByName', header: 'ConsumptionBy', width: 140, text: r => r.consumptionByName ?? '—', render: r => r.consumptionByName ?? '—' },
    { field: 'disposedDate',      header: 'DisposedDate', width: 150, type: 'date', text: r => fmtLocalDateTime(r.disposedDate) ?? '—', render: r => fmtLocalDateTime(r.disposedDate) ?? '—' },
    { field: 'disposedByName',    header: 'DisposedBy',  width: 140, text: r => r.disposedByName ?? '—', render: r => r.disposedByName ?? '—' },
    { field: 'rqidText',          header: 'RQ#',         width: 110, text: r => r.rqidText ?? '—', render: r => r.rqidText ?? '—' },
    { field: 'purchaseOrderNo',   header: 'PO#',         width: 110, text: r => r.purchaseOrderNo ?? '—', render: r => r.purchaseOrderNo ?? '—' },
    { field: 'receiveId',         header: 'RC#',         width: 110, text: r => r.receiveId ?? '—', render: r => r.receiveId ?? '—' },
    { field: 'vendorLt',          header: 'VendorLT',    width: 110, text: r => r.vendorLt ?? '—', render: r => r.vendorLt ?? '—' },
    { field: 'expiryDate',        header: 'ExpirationDate',  width: 130, type: 'date', text: r => fmtLocalDateTime(r.expiryDate) ?? '—', render: r => fmtLocalDateTime(r.expiryDate) ?? '—' },
    { field: 'packUnit',          header: 'PackUnits',   width: 100, text: r => r.packUnit ?? '—', render: r => r.packUnit ?? '—' },
    { field: 'unitPrice',         header: 'UnitPrice',   width: 100, type: 'number', align: 'right', text: r => r.unitPrice == null ? '—' : r.unitPrice.toFixed(2), value: r => r.unitPrice ?? 0 },
  ];

  /** Legacy `LTReportAction_Execute`: prints the checked row(s) as a
   *  one-page-per-lot label PDF; shows the legacy "select a row" message
   *  (`Messages\LDMMessages\selectchkbox`) when nothing is checked. */
  async function handleLabelReport() {
    if (selected.size === 0) {
      notify.warning('Select at least one row to print a label report.');
      return;
    }
    setPrinting(true);
    try {
      await downloadLabelReport(Array.from(selected).map(String));
    } catch {
      notify.error('Failed to generate the label report.');
    } finally {
      setPrinting(false);
    }
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Tracking</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Item Transaction History</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-transaction-history" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <button
                type="button"
                className="ca-btn"
                onClick={() => { void handleLabelReport(); }}
                disabled={selected.size === 0 || printing}
                title="Print a label report (QR + item + LT# + dates + storage) for the selected lot(s)"
              >
                <i className="ti ti-qrcode" /> Label Report
              </button>
            }
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'item-transaction-history', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search LT#, item, vendor…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} lot(s)</span>
          {/* The ceiling must never be silent: before 2026-10-05 this header
              showed the true total while the grid below paged a truncated
              2,000, with no way to tell. */}
          {icmTruncationNote(rows.length, total) && (
            <span className="ica-note ica-note-warn">{icmTruncationNote(rows.length, total)}</span>
          )}
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<TransactionHistoryRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No lots with an LT# found."
            />
          )}
        </div>
      </div>
    </div>
  );
}
